import React, { useEffect, useState } from 'react';
import { brands as brandsApi, bankAccounts as bankAccountsApi } from '@/utils/api';
import { Plus, Trash2, Save, X, Edit2, CreditCard, Globe } from 'lucide-react';
import { toast } from 'sonner';

export default function BankAccountsPanel() {
  const [list, setList] = useState([]);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);

  const reload = () => bankAccountsApi.list().then(r => setList(r.data)).catch(() => {});
  useEffect(() => {
    reload();
    brandsApi.list().then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const newAcct = () => setEditing({
    nickname: '', country: '', country_code: '', currency: '',
    account_holder: '', bank_name: '', rows: [{ label: '', value: '' }],
    brand_id: null, is_default: false, notes: '',
  });

  const save = async () => {
    if (!editing.nickname || !editing.country_code || !editing.bank_name || !editing.account_holder) {
      return toast.error('Nickname, country, bank name and account holder are required.');
    }
    try {
      const payload = {
        ...editing,
        rows: (editing.rows || []).filter(r => r.label && r.value),
      };
      if (editing.id) {
        const { id, ...upd } = payload;
        await bankAccountsApi.update(id, upd);
      } else {
        await bankAccountsApi.create(payload);
      }
      toast.success('Saved');
      setEditing(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this bank account? Invoices already issued will keep their snapshot.')) return;
    await bankAccountsApi.delete(id);
    reload();
  };

  const patchRow = (i, field, value) => setEditing(ed => ({
    ...ed,
    rows: (ed.rows || []).map((r, idx) => idx === i ? { ...r, [field]: value } : r),
  }));
  const addRow = () => setEditing(ed => ({ ...ed, rows: [...(ed.rows || []), { label: '', value: '' }] }));
  const delRow = (i) => setEditing(ed => ({ ...ed, rows: (ed.rows || []).filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-3" data-testid="bank-accounts-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ color: 'var(--ink)' }}>Bank Accounts</h2>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>Wise-style multi-country accounts. Pick one by country when creating an invoice.</p>
        </div>
        <button className="btn-primary flex items-center gap-1.5 text-xs px-3 py-1.5" onClick={newAcct} data-testid="new-bank-account-btn">
          <Plus size={13} /> New account
        </button>
      </div>

      {list.length === 0 ? (
        <div className="card-base p-10 text-center">
          <CreditCard size={30} style={{ color: 'var(--border)', margin: '0 auto 10px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No bank accounts yet</p>
          <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Add one per country you receive payments in (Wise gives you different local details for USD, GBP, EUR, etc.)</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {list.map(a => {
            const brand = brands.find(b => b.id === a.brand_id);
            return (
              <div key={a.id} className="card-base p-4" data-testid={`bank-${a.id}`}>
                <div className="flex items-start justify-between mb-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <p className="font-black text-sm" style={{ color: 'var(--ink)' }}>{a.nickname}</p>
                      {a.is_default && <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>default</span>}
                      <span className="badge-status" style={{ background: '#E6F1F4', color: '#006080' }}>{a.country_code} · {a.currency}</span>
                      {brand && <span className="badge-status" style={{ background: '#5B2D8E15', color: '#5B2D8E' }}>{brand.name}</span>}
                      {!a.brand_id && <span className="badge-status" style={{ background: 'var(--bg)', color: 'var(--muted)' }}>agency-wide</span>}
                    </div>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>{a.bank_name} · {a.account_holder}</p>
                  </div>
                </div>
                <div className="text-xs font-mono space-y-0.5 pl-0.5 mt-2" style={{ color: 'var(--ink)' }}>
                  {(a.rows || []).slice(0, 4).map((r, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="font-semibold" style={{ color: 'var(--muted)' }}>{r.label}:</span>
                      <span className="truncate">{r.value}</span>
                    </div>
                  ))}
                  {(a.rows || []).length > 4 && <p style={{ color: 'var(--muted)' }}>+{a.rows.length - 4} more…</p>}
                </div>
                <div className="flex gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                  <button onClick={() => setEditing(a)} className="text-xs px-3 py-1 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#E6F1F4', color: '#006080' }} data-testid={`edit-bank-${a.id}`}>
                    <Edit2 size={11} /> Edit
                  </button>
                  <button onClick={() => del(a.id)} className="text-xs p-1 ml-auto" style={{ color: '#B34A3A' }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="bank-editor">
          <div className="w-full max-w-2xl rounded-card my-6 p-5" style={{ background: 'var(--surface)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Globe size={16} style={{ color: '#006080' }} />
                <h3 className="font-black text-lg" style={{ color: 'var(--ink)' }}>{editing.id ? 'Edit account' : 'New bank account'}</h3>
              </div>
              <button onClick={() => setEditing(null)}><X size={18} style={{ color: 'var(--muted)' }} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Nickname</label>
                <input className="input-agency text-sm" placeholder="e.g. Wise USD" value={editing.nickname} onChange={e => setEditing({ ...editing, nickname: e.target.value })} data-testid="bank-nickname" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Scope</label>
                <select className="input-agency text-sm" value={editing.brand_id || ''} onChange={e => setEditing({ ...editing, brand_id: e.target.value || null })} data-testid="bank-scope">
                  <option value="">Agency-wide (all brands)</option>
                  {brands.map(b => <option key={b.id} value={b.id}>Only {b.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Country</label>
                <input className="input-agency text-sm" placeholder="United States" value={editing.country} onChange={e => setEditing({ ...editing, country: e.target.value })} data-testid="bank-country" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>ISO code</label>
                <input className="input-agency text-sm" placeholder="US" maxLength={2} value={editing.country_code} onChange={e => setEditing({ ...editing, country_code: e.target.value.toUpperCase() })} data-testid="bank-country-code" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Currency</label>
                <input className="input-agency text-sm" placeholder="USD" maxLength={3} value={editing.currency} onChange={e => setEditing({ ...editing, currency: e.target.value.toUpperCase() })} data-testid="bank-currency" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Bank name</label>
                <input className="input-agency text-sm" placeholder="Wise" value={editing.bank_name} onChange={e => setEditing({ ...editing, bank_name: e.target.value })} data-testid="bank-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Account holder</label>
                <input className="input-agency text-sm" placeholder="Your Name / Company Ltd" value={editing.account_holder} onChange={e => setEditing({ ...editing, account_holder: e.target.value })} data-testid="bank-holder" />
              </div>
            </div>

            <p className="text-xs font-bold uppercase tracking-wide mb-2 mt-4" style={{ color: 'var(--muted)' }}>Account details</p>
            <p className="text-xs mb-2" style={{ color: 'var(--muted)' }}>
              Add whatever Wise gave you for this currency (Routing / Account number / IBAN / SWIFT / Sort code / IFSC / BSB / Reference, etc.).
            </p>
            <div className="space-y-2">
              {(editing.rows || []).map((r, i) => (
                <div key={i} className="flex gap-2" data-testid={`bank-row-${i}`}>
                  <input className="input-agency text-sm" style={{ flexBasis: 180 }} placeholder="Label (e.g. Routing number)" value={r.label} onChange={e => patchRow(i, 'label', e.target.value)} />
                  <input className="input-agency text-sm flex-1 font-mono" placeholder="Value" value={r.value} onChange={e => patchRow(i, 'value', e.target.value)} />
                  <button onClick={() => delRow(i)} className="p-1" style={{ color: '#B34A3A' }}><Trash2 size={12} /></button>
                </div>
              ))}
              <button onClick={addRow} className="text-xs flex items-center gap-1 px-2 py-1 rounded-btn font-semibold" style={{ background: 'transparent', color: '#006080' }} data-testid="add-bank-row">
                <Plus size={11} /> Add field
              </button>
            </div>

            <div className="mt-4">
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Notes (shown under the account on invoices)</label>
              <textarea className="input-agency text-sm resize-none" rows={2} placeholder="e.g. Please use invoice number as the payment reference." value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} />
            </div>

            <label className="flex items-center gap-2 text-sm mt-3" style={{ color: 'var(--ink)' }}>
              <input type="checkbox" checked={!!editing.is_default} onChange={e => setEditing({ ...editing, is_default: e.target.checked })} data-testid="bank-default" />
              Default for this country/scope
            </label>

            <div className="flex gap-2 pt-4 mt-3" style={{ borderTop: '1px solid var(--border)' }}>
              <button className="flex-1 py-2 text-sm rounded-btn border" onClick={() => setEditing(null)} style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>Cancel</button>
              <button className="flex-1 btn-primary py-2 text-sm flex items-center justify-center gap-1.5" onClick={save} data-testid="save-bank-account">
                <Save size={13} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
